import { ninjaConfig } from '../config/ninja';
import { regionConfig } from '../config/region';
import { hasRequiredConsentFor } from '../consent';
import { TIMESTAMP_PROPERTY, Trackers } from '../const';
import { cookieStorage, getCookieExpirationDate, getCookieName } from '../cookies';
import { getDefaultParams as ninjaGetDefaultParams } from '../core/params';
import { trackError } from '../core/utils';
import { makeMapping, makeObjectMapping } from '../mapping';
import { getCurrentPath, objectToQueryString } from '../utils';

// sceuidjs cookie name
const SCEUIDJS_COOKIE = getCookieName('sceuidjs');
// see https://naspersclassifieds.atlassian.net/browse/TRACK-3433
const COOKIE_FIXED = 'sceuidjs_ok';

// Singleton Promise to ensure cookie is set only once
let setSceuidCookiePromise = null;

// From hydra behavior, we need to keep track of the last current page
let lastTrackedPageName = null;

// Configuration for cookie setting and sending events to Comperia's API
const comperiaConfig = {
  sce_endpoint: regionConfig.custom[ninjaConfig.siteUrl].config[Trackers.Comperia]?.sce_endpoint,
  sce_code: regionConfig.custom[ninjaConfig.siteUrl].config[Trackers.Comperia]?.sce_code,
  sce_sid: regionConfig.custom[ninjaConfig.siteUrl].config[Trackers.Comperia]?.sce_sid,
  endpoint: regionConfig.custom[ninjaConfig.siteUrl].config[Trackers.Comperia]?.endpoint,
  name: regionConfig.custom[ninjaConfig.siteUrl].config[Trackers.Comperia]?.name,
  code: regionConfig.custom[ninjaConfig.siteUrl].config[Trackers.Comperia]?.code,
};

// Function to set the sceuid cookie
async function setSceuidCookie() {
  if (setSceuidCookiePromise) {
    return setSceuidCookiePromise;
  }

  setSceuidCookiePromise = (async () => {
    const sceuid = cookieStorage.get(SCEUIDJS_COOKIE);
    const isFixed = localStorage.getItem(COOKIE_FIXED) === '1';

    if (sceuid && isFixed) {
      return sceuid;
    }

    const dataContent = {
      eventType: 'set_cookie',
      __additional: {
        timestamp: Date.now(),
        sid: comperiaConfig.sce_sid,
        url: location.href,
        referrer: document.referrer,
      },
    };

    const url = `${comperiaConfig.sce_endpoint}/api/scenario/code/remote/score?name=setCookie&key=${comperiaConfig.sce_code}`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
          Accept: 'application/json',
        },
        body: JSON.stringify(dataContent),
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const result = await response.json();

      if (!result.sceuid) {
        throw new Error('No sceuid in response');
      }

      cookieStorage.set(SCEUIDJS_COOKIE, result.sceuid, {
        expires: getCookieExpirationDate(1, 'year'),
        path: '/',
        domain: ninjaConfig.cookieDomain,
      });

      localStorage.setItem(COOKIE_FIXED, '1');
      return result.sceuid;
    } catch (error) {
      trackError('COOKIE_ERROR', 'Comperia', 'setSceuidCookie', error.message);
      return null;
    }
  })();

  return setSceuidCookiePromise;
}

// Internal function - Init the tracker
function init(params, callback) {
  if (!hasRequiredConsentFor(Trackers.Comperia)) {
    cookieStorage.remove(SCEUIDJS_COOKIE);
    return;
  }

  if (!comperiaConfig.sce_endpoint || !comperiaConfig.endpoint || !comperiaConfig.code) {
    return;
  }

  // Initialize cookie setting
  setSceuidCookie()
    .then(() => {
      if (callback) callback();
    })
    .catch(error => {
      trackError('INIT_ERROR', 'Comperia', 'init', error.message);
    });
}

/**
 * Gets all default parameters for Hydra.
 */
function getDefaultParams(params) {
  const defaultParams = {};

  // Session
  defaultParams.sl = params.sessionParams?.sessionLong;
  defaultParams.s = params.sessionParams?.session;
  defaultParams.cl = params.sessionParams?.sessionCountLong;
  defaultParams.c = params.sessionParams?.sessionCount;

  // Timestamp - should be generated when the event is added to the dataLayer
  defaultParams.t = params.customParams?.[TIMESTAMP_PROPERTY] ?? Date.now();

  // Discover some params from the url
  if (params.invite) {
    defaultParams.iv = params.invite;
  }

  // Host
  if (params.host) {
    defaultParams.host = params.host;
  }

  // Hash
  if (params.hash) {
    defaultParams.hash = params.hash;
  }

  // Referrer
  if (params.referrer) {
    defaultParams.referer = params.referrer;
  }

  defaultParams.ivd = 'olx-pl_organic';

  if (params.pageName) {
    lastTrackedPageName = getCurrentPath();
  }

  if (lastTrackedPageName) {
    defaultParams.cP = lastTrackedPageName;
  }

  defaultParams.js = 1;

  return defaultParams;
}

function getParams(params, eventKey, eventValue, sceuid) {
  let urlParams = {};
  let eventNameMapping = makeMapping(Trackers.Comperia, eventKey, eventValue);

  if (eventNameMapping.key === false || eventNameMapping.value === false) {
    return urlParams;
  }

  const eventParams = {
    [eventNameMapping.key]: eventNameMapping.value,
  };

  const comperiaParams = {
    name: comperiaConfig.name,
    key: comperiaConfig.code,
    sceuid: sceuid,
  };

  const moduleDefaultParams = getDefaultParams(params);
  const ninjaDefaultParams = makeObjectMapping(Trackers.Comperia, ninjaGetDefaultParams(params));
  const customParams = makeObjectMapping(Trackers.Comperia, params.customParams);

  // Encode custom params. Check first for name clash with default params
  for (const [key, value] of Object.entries(customParams)) {
    if (!moduleDefaultParams[key] && !ninjaDefaultParams[key]) {
      if (Array.isArray(value)) {
        urlParams[key] = JSON.stringify(value);
      } else {
        urlParams[key] = value;
      }
    }
  }

  urlParams = Object.assign({}, urlParams, moduleDefaultParams, ninjaDefaultParams, eventParams, comperiaParams);
  return urlParams;
}

async function doTracking(params) {
  const queryString = objectToQueryString(params);
  const url = comperiaConfig.endpoint + '?' + queryString;

  // Send the request to Comperia API
  await window.fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function trackEvent(params) {
  init(params, async () => {
    try {
      const sceuid = cookieStorage.get(SCEUIDJS_COOKIE);
      if (!sceuid) {
        trackError('COOKIE_ERROR', 'Comperia', 'trackEvent', 'sceuid cookie not found');
        return;
      }

      const eventData = [];
      if (params.eventData.category) {
        eventData.push(params.eventData.category);
      }
      if (params.eventData.action) {
        eventData.push(params.eventData.action);
      }
      if (params.eventData.label) {
        eventData.push(params.eventData.label);
      }
      if (params.eventData.value) {
        eventData.push(params.eventData.value);
      }
      let eventName = convertString(eventData.join('_'));

      const trackingParams = getParams(params, 'trackEvent', eventName, sceuid);
      if (!trackingParams || Object.keys(trackingParams).length === 0) {
        return;
      }

      await doTracking(trackingParams);
    } catch (error) {
      trackError('EXECUTION_ERROR', 'Comperia', 'trackEvent', error.message);
    }
  });
}

export async function trackLinkEvent(params) {
  await trackEvent(params);
}

// Entry function - Track a page
export async function trackPage(params) {
  init(params, async () => {
    try {
      const sceuid = cookieStorage.get(SCEUIDJS_COOKIE);
      if (!sceuid) {
        trackError('COOKIE_ERROR', 'Comperia', 'trackPage', 'sceuid cookie not found');
        return;
      }

      const trackingParams = getParams(params, 'trackPage', params.pageName, sceuid);
      if (!trackingParams || Object.keys(trackingParams).length === 0) {
        return;
      }

      await doTracking(trackingParams);
    } catch (error) {
      trackError('EXECUTION_ERROR', 'Comperia', 'trackPage', error.message);
    }
  });
}

function convertString(text) {
  if (typeof text === 'string') {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\/_]/g, '-')
      .replace(/\s{2}/g, ' ')
      .replace(/\s/g, '-')
      .replace(/---/g, '-')
      .replace(/---/g, '-')
      .replace(/--/g, '-');
  }
  return text;
}
